"use client";

import Link, { type LinkProps } from "next/link";
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import { usePageTransition } from "@/components/page-transition-provider";

type TransitionLinkProps = Omit<
	AnchorHTMLAttributes<HTMLAnchorElement>,
	keyof LinkProps | "href"
> &
	LinkProps & {
		children: ReactNode;
		href: string;
	};

function isModifiedEvent(event: MouseEvent<HTMLAnchorElement>) {
	return (
		event.metaKey ||
		event.ctrlKey ||
		event.shiftKey ||
		event.altKey ||
		event.button !== 0
	);
}

export function TransitionLink({
	children,
	href,
	onClick,
	target,
	...props
}: TransitionLinkProps) {
	const { navigate } = usePageTransition();

	return (
		<Link
			{...props}
			href={href}
			onClick={(event) => {
				onClick?.(event);

				if (
					event.defaultPrevented ||
					isModifiedEvent(event) ||
					target === "_blank" ||
					href.startsWith("mailto:") ||
					href.startsWith("tel:")
				) {
					return;
				}

				event.preventDefault();
				void navigate(href, {
					origin: {
						x: event.clientX,
						y: event.clientY,
					},
				});
			}}
			scroll={false}
			target={target}
		>
			{children}
		</Link>
	);
}
